import * as React from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import ThemeProvider from 'threads5/themes/theme-provider';

import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import type { HeroElement } from 'src/contexts/FirstHeroContext/FirstHeroContext';
import { Wrapper } from 'src/containers';
import { Column, Stack } from 'src/components-v2/Layout';
import Reversable from 'src/components-v2/Layout/ReversableBreakpoint/Reversable';
import { HeroImageOrientations, HeroImageWidths } from 'src/lib/constants';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { CmsMediaSwitch } from 'src/components-v2/Media/CmsMediaSwitch';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';

import {
  buttonGroupStyles,
  embeddedButtonStyles,
} from 'src/sections/Heroes/CmsHeroSwitch/button-group.styles';

import styles from './CmsLargeAssetCenteredHero.module.scss';
import {
  eyebrowStyles,
  headingStyles,
  paragraphStyles,
} from './CmsLargeAssetCenteredHero.styles';

// subtracting the grid's own row spacing nets the rendered gap to exactly px
const gapCalc = (px: number) => {
  return `calc(${px}px - var(--Grid-rowSpacing))`;
};

const gapMb = (lgPx: number) => {
  return {
    xs: gapCalc(32),
    sm: gapCalc(48),
    lg: gapCalc(lgPx),
  };
};

const CmsLargeAssetCenteredHero = ({
  fields,
  sys,
  component = 'header',
}: {
  fields;
  sys;
  component?: HeroElement;
}) => {
  const mode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: fields?.backgroundColor,
  });
  const componentTheme = useComponentTheme(mode);
  const cmsTheme = useCmsTheme();
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const { asset } = fields || {};
  // Anything other than an explicit "Top" (including unset/null on existing
  // entries) renders the established bottom orientation; likewise only an
  // explicit "Full Width" breaks the image out of the container.
  const isImageTop = fields?.imageOrientation === HeroImageOrientations.TOP;
  const isImageFullWidth = fields?.imageWidth === HeroImageWidths.FULL_WIDTH;
  // 2x the max width each mode renders at through the lg source tier
  // (<=1439px viewports): full-bleed 1440, cropped 24 cols
  const lgAssetWidth = isImageFullWidth ? 2880 : 3300;
  const background =
    componentTheme.palette[fields?.backgroundColor] ||
    fields?.backgroundColor?.toLowerCase() ||
    'transparent';
  const fieldId = 'LargeImageBody';

  const assetMedia = asset && (
    <CmsMediaSwitch
      asset={{
        ...asset.fields,
        isLazy: false,
        styles: {
          height: 'auto',
          width: '100%',
          ...asset.fields?.styles,
          // Break the image out of the container, centered on the viewport
          // (body clips overflow-x globally, so 100vw is safe). Applied after
          // the per-entry CMS styles so the breakout is all-or-nothing: a
          // stray width override must not detach it from the negative margin.
          ...(isImageFullWidth && {
            width: '100vw',
            maxWidth: '100vw',
            marginLeft: 'calc(50% - 50vw)',
          }),
        },
      }}
      queryString={{
        xs: `?fm=webp&w=824&q=60`,
        sm: `?fm=webp&w=1226&q=60`,
        md: `?fm=webp&w=1886&q=60`,
        lg: `?fm=webp&w=${lgAssetWidth}&q=60`,
        // Only full-bleed needs a wider tier for >=1440px viewports; the
        // other modes keep serving the lg width there via the src fallback.
        ...(isImageFullWidth && { xl: `?fm=webp&w=3840&q=60` }),
      }}
      width={677} // setting a default width in case it was not provided in CMS
      height={513} // setting a default height in case it was not provided in CMS
      sys={asset.sys}
    />
  );

  // The bottom column renders even without an asset: dozens of live
  // asset-less entries (webinar pages) rely on the empty column's spacing.
  // Top is new, so it can skip the column and avoid a spacer above the
  // heading.
  const assetColumn = isImageTop ? (
    assetMedia && (
      <Column xs={24} sx={{ mb: gapMb(64) }}>
        {assetMedia}
      </Column>
    )
  ) : (
    <Column xs={24}>{assetMedia}</Column>
  );

  const textColumn = (
    <Column
      className={styles.headColumn}
      xs={24}
      sm={18}
      lg={16}
      lgOffset={4}
      sx={
        isImageTop
          ? undefined
          : {
              // the spec's 48/80 tiers describe the gap to the image;
              // asset-less entries keep their pre-existing flat 32px
              mb: assetMedia ? gapMb(80) : gapCalc(32),
            }
      }
    >
      <Stack
        className={styles.headColumn__stack}
        sx={{
          '& .eyebrow-heading': {
            mb: '3.2rem',
          },
          '& h1:not(:last-child), & h2:not(:last-child), & h3:not(:last-child), & h4:not(:last-child), & h5:not(:last-child)':
            {
              mb: { xs: '2.4rem', lg: '1.6rem' },
            },
          '& p': {
            mb: '2.4rem',
            'max-width': { sm: '50.3rem', lg: '80.7rem' },
          },
          '& .button-group': {
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: '1.2rem', lg: '2.6rem' },
            '& a': {
              letterSpacing: { lg: '-0.03rem' },
            },
            ...buttonGroupStyles?.[cmsTheme],
          },
          ...embeddedButtonStyles?.[cmsTheme],
          '& p:nth-last-of-type(1):empty': {
            display: 'none',
          },
        }}
      >
        <RichTextSwitch
          sys={sys}
          content={fields?.centerColumn}
          nodes={{
            h1: {
              component: 'h1',
              variant: 'h1',
              fieldId,
              sx: {
                ...headingStyles?.[cmsTheme],
              },
            },
            h2: {
              component: 'h2',
              variant: 'h1',
              fieldId,
              sx: {
                ...headingStyles?.[cmsTheme],
              },
            },
            h6: {
              component: 'h1',
              variant: 'h6',
              className: 'eyebrow-heading',
              fieldId,
              sx: {
                ...eyebrowStyles?.[cmsTheme],
                fontSize: '2.0rem',
              },
            },
            p: {
              component: 'p',
              variant: 'h5',
              fieldId,
              sx: {
                ...paragraphStyles?.[cmsTheme],
              },
            },
          }}
        />
      </Stack>
    </Column>
  );

  return (
    <ThemeProvider theme={componentTheme}>
      <Wrapper
        component={component}
        sx={{ background: background, ...fields?.styles }}
        {...inspectorProps({ fieldId: 'internalTitle' })}
      >
        <Reversable
          reversed={isImageTop}
          first={textColumn}
          second={assetColumn}
        />
      </Wrapper>
    </ThemeProvider>
  );
};

export default CmsLargeAssetCenteredHero;
